import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  freimeldungFehlerCode,
  ladeFreimeldungFotoHoch,
  ladeFreimeldungPublic,
  meldeFreimeldung,
  type FreimeldungPublic,
  type FreimeldungPublicItem,
} from '../../../services/freimeldungPublicService';
import { bildFehlerText, compressImage } from '../../../services/imageService';
import { describeError } from '../../../services/errorMessage';
import {
  LEERES_FORMULAR,
  OHNE_FIRMA,
  fehlerTextFuer,
  filtereNachFirma,
  firmenAuswahl,
  firmenKnoepfe,
  fotoFehlerText,
  kontaktAus,
  naechsterMangel,
  parseZustand,
  pruefeFormular,
  pruefeFormularFelder,
  serialisiereZustand,
  speicherSchluessel,
  startFirmenAuswahl,
  texteFuer,
  wirksameFirmenAuswahl,
  type FreimeldungFeldFehler,
  type FreimeldungFormular,
  type MeldeArt,
  type Sprache,
} from './freimeldungLogic';

/**
 * Zustand und Handlungen der oeffentlichen Melderseite #/freimeldung/<token>
 * (docs/freimeldung-online-plan.md §E).
 *
 * Kein Login, keine Tabelle: nur `freimeldungPublicService`. Die Angaben zur
 * Person liegen im sessionStorage je Token (`osia.freimeldung.<token>`), damit
 * der zweite Mangel nicht noch einmal getippt werden muss. Das Foto wird NIE
 * gespeichert — es geht als JPEG-DataURL an /api/freimeldung-foto und lebt von
 * da an nur als URL weiter.
 *
 * Nach einer erfolgreichen Meldung wird das Blatt neu geholt, damit der Chip
 * „bereits gemeldet (n)" den frischen Stand zeigt.
 */

export type FreimeldungSchritt = 'laedt' | 'ungueltig' | 'fehler' | 'liste' | 'formular' | 'sendet' | 'ergebnis';

/** Was die Ergebniskarte braucht — der Mangel selbst kann danach verschwinden. */
export interface FreimeldungAbschluss {
  bereits: boolean;
  gemeldetAt: string;
  titel: string;
  nummer: number | null;
  /** Freimeldung oder Zurueckweisung — die Quittung sagt, was eingegangen ist. */
  art: MeldeArt;
}

/** Zielgroesse des Beweisfotos: 1024 px lange Kante, JPEG-Qualitaet 0,6 — reicht
 *  fuer den Nachweis und bleibt unter der 1,5-MB-Grenze der Upload-Route. */
const FOTO_BREITE = 1024;
const FOTO_QUALITAET = 0.6;

const leseGespeichert = (token: string): FreimeldungFormular => {
  try {
    const k = parseZustand(sessionStorage.getItem(speicherSchluessel(token)));
    return k ? { ...LEERES_FORMULAR, ...k } : LEERES_FORMULAR;
  } catch {
    // Privater Modus / gesperrter Speicher: die Seite laeuft ohne Persistenz weiter.
    return LEERES_FORMULAR;
  }
};

const speichere = (token: string, f: FreimeldungFormular): void => {
  try {
    sessionStorage.setItem(speicherSchluessel(token), serialisiereZustand(kontaktAus(f)));
  } catch {
    // s. o. — ein voller oder gesperrter Speicher darf die Meldung nicht stoppen.
  }
};

export const useFreimeldung = (token: string) => {
  const [schritt, setSchritt] = useState<FreimeldungSchritt>('laedt');
  const [ladeFehler, setLadeFehler] = useState('');
  const [daten, setDaten] = useState<FreimeldungPublic | null>(null);
  const [formular, setFormular] = useState<FreimeldungFormular>(() => leseGespeichert(token));
  const [auswahlId, setAuswahlId] = useState<string | null>(null);
  /** Was zum gewaehlten Mangel abgegeben wird: Freimeldung oder Zurueckweisung. */
  const [art, setArt] = useState<MeldeArt>('behoben');
  /**
   * Firmen-Schnellauswahl ueber der Liste; `null` = alle. `undefined` heisst
   * „noch nicht entschieden": beim ersten Blatt setzt der Hook die Firma aus
   * dem Kontaktspeicher, danach gilt nur noch der Klick des Melders.
   */
  const [firmaFilter, setFirmaFilter] = useState<string | null | undefined>(undefined);
  const [fehler, setFehler] = useState<string | null>(null);
  const [feldFehler, setFeldFehler] = useState<FreimeldungFeldFehler>({});
  const [fotoLaedt, setFotoLaedt] = useState(false);
  const [ergebnis, setErgebnis] = useState<FreimeldungAbschluss | null>(null);

  /**
   * Riegel gegen den zweiten Fingertipp auf „Meldung absenden": `schritt`
   * steht erst nach dem naechsten Render, zwei schnelle Beruehrungen liefen
   * davor beide durch (Muster useEinweisungTeilnahme).
   */
  const laeuft = useRef(false);

  /**
   * Der gerade gewaehlte Mangel, auch fuer Rueckmeldungen, die nach einem
   * `await` eintreffen (Foto-Upload). Ohne diesen Spiegel landet ein Foto,
   * das waehrend des Hochladens „verlassen" wurde, beim naechsten Mangel.
   */
  const auswahlIdRef = useRef<string | null>(null);

  /**
   * Zaehler der Vorgaenge: steigt bei JEDEM Zuruecksetzen von Kommentar und
   * Foto (anderer Mangel, andere Art, „Weiteren Mangel melden"). Ein Foto,
   * das waehrend des Hochladens seinen Vorgang verloren hat, verfaellt — die
   * Mangel-Id allein reicht nicht: beim Wechsel von „Als behoben melden" auf
   * „Zurückweisen" am SELBEN Mangel bliebe sie gleich, und das Foto der einen
   * Meldung landete in der anderen.
   */
  const vorgang = useRef(0);

  /**
   * Zaehler der Ladelaeufe. JEDER Abruf des Blattes (erster Aufruf wie
   * Nachladen nach einer Meldung) zieht eine Nummer; nur das Ergebnis der
   * zuletzt gezogenen darf noch in den Zustand. Sonst ueberschriebe eine
   * langsame alte Antwort den frischen Stand — sichtbar am Chip „bereits
   * gemeldet (n)", der dann wieder zurueckspringt.
   */
  const ladeLauf = useRef(0);

  /** Ist die Seite noch da? Antworten nach dem Abbau werden verworfen. */
  const montiert = useRef(true);
  useEffect(() => {
    montiert.current = true;
    return () => {
      montiert.current = false;
    };
  }, []);

  const sprache: Sprache = daten?.sprache === 'en' ? 'en' : 'de';
  const t = texteFuer(sprache);
  const fotoPflicht = daten?.fotoPflicht === true;

  useEffect(() => {
    let aktiv = true;
    // Auch der erste Abruf zieht eine Nummer: ein Nachladen aus einer
    // vorigen Meldung darf das Blatt des NEUEN Tokens nicht ueberschreiben.
    const lauf = ++ladeLauf.current;
    setSchritt('laedt');
    ladeFreimeldungPublic(token)
      .then((d) => {
        if (!aktiv || lauf !== ladeLauf.current) return;
        // null heisst: Token unbekannt, Link abgelaufen ODER Migration fehlt —
        // der Melder bekommt in allen Faellen denselben Satz.
        if (!d) {
          setSchritt('ungueltig');
          return;
        }
        setDaten(d);
        setSchritt('liste');
      })
      .catch((e) => {
        if (!aktiv || lauf !== ladeLauf.current) return;
        setLadeFehler(describeError(e));
        setSchritt('fehler');
      });
    return () => {
      aktiv = false;
    };
  }, [token]);

  useEffect(() => {
    speichere(token, formular);
  }, [token, formular]);

  useEffect(() => {
    auswahlIdRef.current = auswahlId;
  }, [auswahlId]);

  const items: FreimeldungPublicItem[] = useMemo(() => daten?.items ?? [], [daten]);
  const firmen = useMemo(() => firmenAuswahl(items, daten?.firmen ?? []), [items, daten]);
  const ausgewaehlt = useMemo(
    () => (auswahlId ? items.find((i) => i.id === auswahlId) ?? null : null),
    [items, auswahlId],
  );
  const firmenFilterKnoepfe = useMemo(() => firmenKnoepfe(items), [items]);

  // Die Startauswahl, sobald das erste Blatt da ist: die gespeicherte Firma,
  // wenn sie im Bericht vorkommt. Nur EINMAL — danach entscheidet der Melder.
  useEffect(() => {
    if (firmaFilter !== undefined || !daten) return;
    setFirmaFilter(startFirmenAuswahl(formular.firma, firmenFilterKnoepfe));
  }, [daten, firmaFilter, formular.firma, firmenFilterKnoepfe]);

  // Was WIRKLICH gefiltert wird: faellt die gewaehlte Firma beim Nachladen
  // weg (ihr letzter Mangel ist geschlossen) oder gibt es gar keine Knoepfe
  // mehr, gilt „Alle" — sonst stuende eine leere Liste da, ohne Knopf, der
  // sie zuruecksetzt.
  const wirksamerFilter = useMemo(
    () => wirksameFirmenAuswahl(firmaFilter ?? null, firmenFilterKnoepfe),
    [firmaFilter, firmenFilterKnoepfe],
  );
  const sichtbareItems = useMemo(
    () => filtereNachFirma(items, wirksamerFilter),
    [items, wirksamerFilter],
  );

  /**
   * Schnellauswahl „Ihre Firma". Eine gewaehlte Firma wird zugleich ins
   * Formular uebernommen — wer „meine Firma" antippt, meldet auch als sie.
   */
  const waehleFirmaFilter = useCallback((wert: string | null) => {
    setFirmaFilter(wert);
    if (wert && wert !== OHNE_FIRMA) setFormular((f) => ({ ...f, firma: wert }));
  }, []);

  /**
   * Mangel waehlen — zum Melden (`behoben`) oder Zurueckweisen. Beim Wechsel
   * auf einen ANDEREN Mangel ODER eine andere Art fallen Kommentar und Foto
   * weg — genau wie nach „Weiteren Mangel melden": beide gehoerten zum
   * vorigen Vorgang (ein Arbeitskommentar ist keine Begruendung), und ein
   * fremdes Beweisfoto wuerde sonst mitgeschickt. Firma und Kontakt bleiben
   * stehen. Derselbe Mangel mit derselben Art noch einmal gewaehlt (Blick in
   * die Liste, dann zurueck) behaelt seine Eingaben.
   */
  const waehleItem = useCallback(
    (id: string, neueArt: MeldeArt = 'behoben') => {
      if (id !== auswahlId || neueArt !== art) {
        vorgang.current += 1;
        setFormular((f) => naechsterMangel(f));
      }
      setAuswahlId(id);
      setArt(neueArt);
      setFehler(null);
      setFeldFehler({});
      setSchritt('formular');
    },
    [auswahlId, art],
  );

  const zurueckZurListe = useCallback(() => {
    setFehler(null);
    setFeldFehler({});
    setSchritt('liste');
  }, []);

  const setzeFeld = useCallback(<K extends keyof FreimeldungFormular>(feld: K, wert: FreimeldungFormular[K]) => {
    setFormular((f) => ({ ...f, [feld]: wert }));
  }, []);

  /**
   * Foto: erst im Browser auf 1024 px/JPEG eindampfen (ein iPhone-Bild hat
   * sonst 4 MB und die Route weist es ab), dann ueber die Vercel-Route in den
   * Bucket. Der Browser sieht nur die fertige URL.
   */
  const fotoWaehlen = useCallback(
    async (file: File | null | undefined) => {
      if (!file) return;
      // Zu welchem Mangel das Foto gehoert. Wird waehrend des Hochladens ein
      // anderer Mangel gewaehlt, verfaellt das Ergebnis — sonst haengte das
      // Beweisfoto am falschen Mangel.
      const fuerId = auswahlIdRef.current;
      const fuerVorgang = vorgang.current;
      const verfallen = () => auswahlIdRef.current !== fuerId || vorgang.current !== fuerVorgang;
      setFotoLaedt(true);
      setFehler(null);
      try {
        // Nachweisfoto darf nicht beschnitten werden — cropToPortrait: false
        // (Standard waere ein zentraler 3:4-Ausschnitt, gedacht fuer Begehungsfotos).
        const dataUrl = await compressImage(file, FOTO_BREITE, FOTO_QUALITAET, { cropToPortrait: false });
        const url = await ladeFreimeldungFotoHoch(token, dataUrl);
        if (verfallen()) return;
        setFormular((f) => ({ ...f, fotoUrl: url }));
        setFeldFehler((ff) => ({ ...ff, fotoUrl: undefined }));
      } catch (e) {
        if (verfallen()) return;
        setFormular((f) => ({ ...f, fotoUrl: null }));
        // Ein iPhone-HEIC, das der Decoder nicht schafft, bekommt seinen eigenen
        // Hinweistext (Einstellungen › Kamera › Formate) — sonst der Satz zum Upload.
        setFehler(bildFehlerText(e, sprache, fotoFehlerText(describeError(e), sprache)));
      } finally {
        setFotoLaedt(false);
      }
    },
    [token, sprache],
  );

  const fotoEntfernen = useCallback(() => {
    setFormular((f) => ({ ...f, fotoUrl: null }));
    setFehler(null);
  }, []);

  /**
   * Das Blatt im Hintergrund nachziehen — fuer den Chip „bereits gemeldet (n)".
   *
   * LAEUFT NEBENHER, NICHT IM ABSENDEN: Solange dieser Abruf im `absenden`
   * mitlief, blieb der Riegel `laeuft` gesetzt, und der naechste Fingertipp auf
   * „Meldung absenden" nach „Weiteren Mangel melden" fiel lautlos aus. Faellt
   * das Nachladen aus, bleibt das alte Blatt stehen — die Quittung haengt nicht
   * daran.
   */
  const blattNachziehen = useCallback(async () => {
    const lauf = ++ladeLauf.current;
    try {
      const neu = await ladeFreimeldungPublic(token);
      // Verfallen: ein neuerer Abruf laeuft schon oder die Seite ist weg.
      if (!montiert.current || lauf !== ladeLauf.current) return;
      if (neu) setDaten(neu);
    } catch {
      // still: der Melder ist fertig, die Quittung steht.
    }
  }, [token]);

  /**
   * Absenden. Die Pruefung laeuft zuerst im Client (dieselben Grenzen wie die
   * CHECKs), die RPC bleibt der Riegel. P0002 heisst: der Link ist inzwischen
   * zu — dann zeigt die ganze Seite „nicht mehr gültig" statt eines
   * Fehlerkastens.
   *
   * DER RIEGEL FAELLT MIT DER RPC, nicht mit dem Nachladen: `schritt` steht in
   * jedem Zweig fest (ergebnis / formular / ungueltig), bevor `laeuft` wieder
   * offen ist — die Seite kann also nicht in „sendet" stehen bleiben, und der
   * naechste Mangel laesst sich sofort melden.
   */
  const absenden = useCallback(async () => {
    if (laeuft.current) return;
    const id = auswahlId;
    if (!id) {
      setSchritt('liste');
      return;
    }

    const meldungen = pruefeFormular(formular, fotoPflicht, sprache, art);
    if (meldungen.length > 0) {
      setFeldFehler(pruefeFormularFelder(formular, fotoPflicht, sprache, art));
      setFehler(t.fehlerSammel);
      setSchritt('formular');
      return;
    }

    laeuft.current = true;
    setFeldFehler({});
    setFehler(null);
    setSchritt('sendet');
    try {
      const antwort = await meldeFreimeldung(token, {
        itemId: id,
        firma: formular.firma,
        name: formular.name,
        email: formular.email,
        phone: formular.phone,
        kommentar: formular.kommentar,
        fotoUrl: formular.fotoUrl,
        art,
      });
      setErgebnis({
        bereits: antwort.bereits === true,
        gemeldetAt: antwort.gemeldetAt,
        titel: ausgewaehlt?.title ?? '',
        nummer: ausgewaehlt?.ticketNumber ?? null,
        art,
      });
      setSchritt('ergebnis');
    } catch (e) {
      const code = freimeldungFehlerCode(e);
      if (code === 'P0002') {
        setSchritt('ungueltig');
      } else {
        setFehler(fehlerTextFuer(code, describeError(e), sprache));
        setSchritt('formular');
      }
      // Der Riegel faellt im `finally`; hier ist nichts nachzuziehen.
      return;
    } finally {
      laeuft.current = false;
    }

    // Erst jetzt — ohne Riegel und ohne `await` im Sendeweg.
    void blattNachziehen();
  }, [auswahlId, formular, fotoPflicht, sprache, art, t.fehlerSammel, token, ausgewaehlt, blattNachziehen]);

  /**
   * „Weiteren Mangel melden": zurueck zur Liste, Firma und Kontakt bleiben
   * stehen (meist meldet dieselbe Person mehrere Maengel), Kommentar und Foto
   * sind weg — die gehoerten zum vorigen Mangel.
   */
  const weiterenMelden = useCallback(() => {
    vorgang.current += 1;
    setFormular((f) => naechsterMangel(f));
    setAuswahlId(null);
    setErgebnis(null);
    setFehler(null);
    setFeldFehler({});
    setSchritt('liste');
  }, []);

  return {
    schritt,
    ladeFehler,
    daten,
    sprache,
    texte: t,
    fotoPflicht,
    items,
    sichtbareItems,
    firmen,
    firmenFilterKnoepfe,
    firmaFilter: wirksamerFilter,
    waehleFirmaFilter,
    art,
    ausgewaehlt,
    formular,
    feldFehler,
    fehler,
    fotoLaedt,
    ergebnis,
    waehleItem,
    zurueckZurListe,
    setzeFeld,
    fotoWaehlen,
    fotoEntfernen,
    absenden,
    weiterenMelden,
  };
};

export type FreimeldungSteuerung = ReturnType<typeof useFreimeldung>;
